import ReactDOM from "react-dom/client";
import { ConfigProvider } from "antd";
import App from "./App";
// Preload critical Satoshi weights so they paint without a swap (FOUT fix).
// Importing as URL lets Vite resolve the hashed filename automatically.
import satoshiVariable from "./fonts/Satoshi-Variable.woff2?url";
import satoshiRegular from "./fonts/Satoshi-Regular.woff2?url";
import satoshiMedium from "./fonts/Satoshi-Medium.woff2?url";

[satoshiVariable, satoshiRegular, satoshiMedium].forEach((href) => {
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "font";
  link.type = "font/woff2";
  link.href = href;
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
});
import "./styles/breakpoints.css";
import "./index.css";
// Eagerly load login styles in the main bundle so the /login route paints
// styled on first render instead of flashing unstyled until the lazy chunk's
// CSS is injected (FOUC fix).
import "./pages/Login/LoginEmailPass.css";
import reportWebVitals from "./reportWebVitals";
import { persistor, store } from "./redux/store";
import { RESET_APP } from "./redux/actions/resetActions";
import {
  clearTokenRenewalIntervals,
  unregisterSSEAbort,
} from "./redux/actions/authActions";
import { setStore } from "./api/instance";
import { setSessionExpiredCleanup } from "./utils/tokenUtils";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PricingModalProvider } from "./contexts/PricingModalContext";
import { FormPersistenceProvider } from "./contexts/FormPersistenceContext";
import TagManager from "react-gtm-module";
import { BrowserRouter as Router } from "react-router-dom";
import { ClearCacheProvider } from "react-clear-cache";
// Imported last so the shared job-description heading rules win cascade ties
// against the page-level CSS loaded via App's `./pages/**/*.css` glob.
import "./styles/jobDescriptionContent.css";

// pdf.js worker path is set by each react-pdf consumer (Applicantprofile,
// ResumeBuilder, ResumeEnhancement) so pdfjs-dist stays out of the entry chunk.

// Vite content-hashes every JS chunk per build (e.g. NewOtpVerificationPage-l18E3iXH.js).
// A tab left open since before a deploy still references the OLD hashed filenames; if it
// then lazy-loads a route (React.lazy/dynamic import()) whose chunk no longer exists
// post-deploy, the server can't serve it and the browser fails to load it as a module.
// Vite exposes this exact failure as a `vite:preloadError` event — reload once to pick up
// the current build instead of leaving the user on a broken page.
// Throttled (not a one-shot flag) so this still helps after a *later* deploy in the same
// long-lived tab, while a genuinely broken chunk can't reload-loop more than once per window.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  const RELOAD_KEY = "haire:chunk-reload-at";
  const RELOAD_THROTTLE_MS = 10000;
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  if (Date.now() - last > RELOAD_THROTTLE_MS) {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();
  }
});

// import * as Sentry from "@sentry/react";

// Break the circular dependency: inject the store into the axios instance
// after both modules have fully evaluated. Interceptors only run at
// request time, so this is always set before any API call is made.
setStore(store);

setSessionExpiredCleanup(() => {
  clearTokenRenewalIntervals();
  unregisterSSEAbort();
  store.dispatch({ type: RESET_APP });
  sessionStorage.clear();
  [
    "persist:seed:main",
    "persist:seed:client",
    "persist:seed:applicant",
    "persist:link-seed",
    "persist:link-seed-ts",
  ].forEach((k) => localStorage.removeItem(k));
});

// Sentry.init({
//   dsn: import.meta.env.VITE_SENTRY_DSN,
//   environment: import.meta.env.VITE_ENVIRONMENT,
//   integrations: [
//     Sentry.browserTracingIntegration(),
//     // See docs for support of different versions of variation of react router
//     // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
//     Sentry.reactRouterV6BrowserTracingIntegration({
//       useEffect: useEffect,
//       useLocation,
//       useNavigationType,
//       createRoutesFromChildren,
//       matchRoutes,
//     }),
//     Sentry.replayIntegration(),
//   ],
//   tracesSampleRate: 1.0,
//   replaysSessionSampleRate: 0.1,
//   replaysOnErrorSampleRate: 1.0,
// });

const tagManagerArgs = {
  gtmId: "G-YQMPREVDG4", // Replace with your GTM ID
};

TagManager.initialize(tagManagerArgs);

const root = ReactDOM.createRoot(document.getElementById("root"));
// Remove Sentry.ErrorBoundary for testing error boundary in App.js
// root.render(
//   <Sentry.ErrorBoundary fallback={"An error has occurred"}>
//     {/* <React.StrictMode> */}
//     <Provider store={store}>
//       <PersistGate loading={null} persistor={persistor}>
//         <PricingModalProvider>
//           <Router>
//             <App />
//           </Router>
//         </PricingModalProvider>
//         <ToastContainer />
//       </PersistGate>
//     </Provider>
//     {/* </React.StrictMode> */}
//   </Sentry.ErrorBoundary>
// );

// Use this instead:
root.render(
  // <React.StrictMode>
  // ClearCacheProvider polls /meta.json (regenerated each build) and, on a new
  // deploy, clears CacheStorage + force-reloads so frequent users don't get
  // stuck on a stale app shell. auto only in production to avoid dev reloads.
  <ClearCacheProvider auto={import.meta.env.PROD}>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
      {/* AntD v5's CSS-in-JS writes its `fontFamily` token (a system-font stack)
          directly onto component elements, overriding the Satoshi inherited from
          body — and its async style injection flipped the font mid-animation,
          causing the Top Up Credits modal's open/close font flicker. Setting the
          token makes AntD render in Satoshi from the first frame, app-wide. */}
      <ConfigProvider
        theme={{ token: { fontFamily: "'Satoshi-Variable', sans-serif" } }}
      >
        <Router>
          <FormPersistenceProvider>
            <PricingModalProvider>
              <App />
            </PricingModalProvider>
          </FormPersistenceProvider>
        </Router>
        <ToastContainer />
      </ConfigProvider>
    </PersistGate>
  </Provider>
  </ClearCacheProvider>
  // </React.StrictMode>
);

// Make store available globally for error logging
if (typeof window !== 'undefined') {
  window.__REDUX_STORE__ = store;
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
